// 'use client'
import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react"

/* =========================
   Defaults
========================= */

const DEFAULTS = {
  initialBoxSize: 360,
  targetSize: "fullscreen",
  scrollHeightVh: 280,
  overlayBlur: 10,
  overlayRevealDelay: 0.35,
  eases: {
    container: "expo.out",
    overlay: "expo.out",
    text: "power3.inOut",
  },
}

/* =========================
   Helpers
========================= */

function isSourceObject(m) {
  return !!m && typeof m !== "string"
}

/* =========================
   Component
========================= */

export const HeroScrollVideo = forwardRef(function HeroScrollVideo({
  title = "Future Forms",
  subtitle = "Design in Motion",
  meta = "2025",
  credits = (
    <>
      <p>Crafted by</p>
      <p>Scott Clayton</p>
    </>
  ),

  media,
  poster,
  mediaType = "video",
  muted = true,
  loop = true,
  playsInline = true,
  autoPlay = false,

  overlay = {
    caption: "PROJECT • 07",
    heading: "Clarity in Motion",
    paragraphs: [
      "Scroll to expand the frame and reveal the story.",
      "Built with GSAP ScrollTrigger and optional Lenis smooth scroll.",
    ],
    extra: null,
  },

  initialBoxSize = DEFAULTS.initialBoxSize,
  targetSize = DEFAULTS.targetSize,
  scrollHeightVh = DEFAULTS.scrollHeightVh,
  showHeroExitAnimation = true,
  sticky = true,
  overlayBlur = DEFAULTS.overlayBlur,
  overlayRevealDelay = DEFAULTS.overlayRevealDelay,
  eases = DEFAULTS.eases,

  smoothScroll = true,
  lenisOptions,

  className,
  style,
}, ref) {
  const rootRef = useRef(null)
  const containerRef = useRef(null)
  const headlineRef = useRef(null)
  const overlayRef = useRef(null)
  const overlayCaptionRef = useRef(null)
  const overlayContentRef = useRef(null)
  const videoRef = useRef(null)

  const [isMuted, setIsMuted] = useState(muted)

  const isClient = typeof window !== "undefined"

  // Toggle mute handler
  const handleToggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted
      setIsMuted(videoRef.current.muted)
    }
  }

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    playFromStart: () => {
      if (videoRef.current) {
        videoRef.current.currentTime = 0
        videoRef.current.muted = false
        setIsMuted(false)
        videoRef.current.play().catch(() => {})
      }
    },
    getVideoElement: () => videoRef.current,
  }))

  // Inline CSS variables for tuning (non-theme)
  const cssVars = useMemo(
    () => ({
      "--initial-size": `${initialBoxSize}px`,
      "--overlay-blur": `${overlayBlur}px`,
    }),
    [initialBoxSize, overlayBlur]
  )

  // Scroll + GSAP wiring
  useEffect(() => {
    if (!isClient) return

    let gsap
    let ScrollTrigger
    let CustomEase
    let LenisCtor
    let lenis

    let heroTl
    let mainTl
    let overlayDarkenEl = null

    let rafCb = null

    let cancelled = false

    ;(async () => {
      const gsapPkg = await import("gsap")
      gsap = gsapPkg.gsap || gsapPkg.default || gsapPkg

      const ScrollTriggerPkg =
        (await import("gsap/ScrollTrigger").catch(() =>
          import("gsap/dist/ScrollTrigger")
        )) || {}
      ScrollTrigger =
        ScrollTriggerPkg.default ||
        ScrollTriggerPkg.ScrollTrigger ||
        ScrollTriggerPkg

      const CustomEasePkg =
        (await import("gsap/CustomEase").catch(() =>
          import("gsap/dist/CustomEase")
        )) || {}
      CustomEase =
        CustomEasePkg.default ||
        CustomEasePkg.CustomEase ||
        CustomEasePkg

      gsap.registerPlugin(ScrollTrigger, CustomEase)

      if (cancelled) return

      if (smoothScroll) {
        const try1 = await import("lenis").catch(() => null)
        LenisCtor = try1?.default || try1?.Lenis
        if (LenisCtor) {
          lenis = new LenisCtor({
            duration: 0.8,
            smoothWheel: true,
            gestureOrientation: "vertical",
            ...lenisOptions,
          })
          rafCb = (time) => lenis?.raf(time * 1000)
          gsap.ticker.add(rafCb)
          gsap.ticker.lagSmoothing(0)
          lenis?.on?.("scroll", ScrollTrigger.update)
        }
      }

      const containerEase = eases.container ?? "expo.out"
      const overlayEase = eases.overlay ?? "expo.out"
      const textEase = eases.text ?? "power3.inOut"

      const container = containerRef.current
      const overlayEl = overlayRef.current
      const overlayCaption = overlayCaptionRef.current
      const overlayContent = overlayContentRef.current
      const headline = headlineRef.current

      // Darkening overlay inside the media box
      if (container) {
        overlayDarkenEl = document.createElement("div")
        overlayDarkenEl.setAttribute("data-auto-darken", "true")
        overlayDarkenEl.style.position = "absolute"
        overlayDarkenEl.style.inset = "0"
        overlayDarkenEl.style.background = "rgba(0,0,0,0)"
        overlayDarkenEl.style.pointerEvents = "none"
        overlayDarkenEl.style.zIndex = "1"
        container.appendChild(overlayDarkenEl)
      }

      // Headline roll-away
      if (showHeroExitAnimation && headline) {
        heroTl = gsap.timeline({
          scrollTrigger: {
            trigger: headline,
            start: "top top",
            end: "top+=420 top",
            scrub: 1.1,
          },
        })

        headline
          .querySelectorAll(".hsv-headline > *")
          .forEach((el, i) => {
            heroTl.to(
              el,
              {
                rotationX: 80,
                y: -36,
                scale: 0.86,
                opacity: 0,
                filter: "blur(4px)",
                transformOrigin: "center top",
                ease: textEase,
              },
              i * 0.08
            )
          })
      }

      // Main sticky expansion timeline
      const triggerEl = rootRef.current?.querySelector(
        "[data-sticky-scroll]"
      )

      if (!triggerEl || !container || !overlayEl) return

      mainTl = gsap.timeline({
        scrollTrigger: {
          trigger: triggerEl,
          start: "top top",
          end: "bottom bottom",
          scrub: 1.1,
        },
      })

      // Target size
      const target = (() => {
        if (targetSize === "fullscreen") {
          return { width: "92vw", height: "92vh", borderRadius: 0 }
        }
        return {
          width: `${targetSize.widthVw ?? 92}vw`,
          height: `${targetSize.heightVh ?? 92}vh`,
          borderRadius: targetSize.borderRadius ?? 0,
        }
      })()

      // Initial states
      gsap.set(container, {
        width: initialBoxSize,
        height: initialBoxSize,
        borderRadius: 20,
        filter: "none",
        clipPath: "inset(0 0 0 0)",
      })
      gsap.set(overlayEl, { clipPath: "inset(100% 0 0 0)" })
      gsap.set(overlayContent, {
        filter: `blur(var(--overlay-blur))`,
        scale: 1.05,
      })
      gsap.set([overlayContent, overlayCaption], { y: 30 })

      // Animate the container to expand
      mainTl
        .to(
          container,
          {
            width: target.width,
            height: target.height,
            borderRadius: target.borderRadius,
            ease: containerEase,
          },
          0
        )
        // Darken as it expands
        .to(
          overlayDarkenEl,
          {
            backgroundColor: "rgba(0,0,0,0.4)",
            ease: "power2.out",
          },
          0
        )
        // Reveal overlay panel
        .to(
          overlayEl,
          {
            clipPath: "inset(0% 0 0 0)",
            backdropFilter: `blur(${overlayBlur}px)`,
            ease: overlayEase,
          },
          overlayRevealDelay
        )
        // Content slides in and unblurs
        .to(overlayCaption, { y: 0, ease: overlayEase }, overlayRevealDelay + 0.05)
        .to(
          overlayContent,
          {
            y: 0,
            filter: "blur(0px)",
            scale: 1,
            ease: overlayEase,
          },
          overlayRevealDelay + 0.05
        )

      // Try to play video
      const videoEl = container.querySelector("video")
      if (videoEl) {
        const tryPlay = () => videoEl.play().catch(() => {})
        tryPlay()
        ScrollTrigger.create({
          trigger: triggerEl,
          start: "top top",
          onEnter: tryPlay,
        })
      }
    })()

    return () => {
      cancelled = true
      try {
        heroTl?.kill?.()
        mainTl?.kill?.()
      } catch {}
      try {
        if (ScrollTrigger?.getAll && rootRef.current) {
          ScrollTrigger
            .getAll()
            .forEach((t) => rootRef.current?.contains(t.trigger) && t.kill(true))
        }
      } catch {}
      try {
        if (overlayDarkenEl?.parentElement) {
          overlayDarkenEl.parentElement.removeChild(overlayDarkenEl)
        }
      } catch {}
      try {
        if (rafCb && gsap?.ticker) {
          gsap.ticker.remove(rafCb)
          gsap.ticker.lagSmoothing(1000, 16)
        }
      } catch {}
      try {
        lenis?.off?.("scroll", ScrollTrigger?.update)
        lenis?.destroy?.()
      } catch {}
    }
  }, [
    isClient,
    initialBoxSize,
    targetSize,
    scrollHeightVh,
    overlayBlur,
    overlayRevealDelay,
    eases.container,
    eases.overlay,
    eases.text,
    showHeroExitAnimation,
    sticky,
    smoothScroll,
    JSON.stringify(lenisOptions),
  ])

  // Media rendering
  const renderMedia = () => {
    if (mediaType === "image") {
      const src = typeof media === "string" ? media : media?.mp4 || ""
      return (
        <img
          src={src}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )
    }
    // video
    const sources = []
    if (typeof media === "string") {
      sources.push(<source key="mp4" src={media} type="video/mp4" />)
    } else if (isSourceObject(media)) {
      if (media.webm) sources.push(<source key="webm" src={media.webm} type="video/webm" />)
      if (media.mp4) sources.push(<source key="mp4" src={media.mp4} type="video/mp4" />)
      if (media.ogg) sources.push(<source key="ogg" src={media.ogg} type="video/ogg" />)
    }

    return (
      <video
        ref={videoRef}
        poster={poster}
        muted={isMuted}
        loop={loop}
        playsInline={playsInline}
        autoPlay={autoPlay || muted}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      >
        {sources}
      </video>
    )
  }

  return (
    <div
      ref={rootRef}
      className={["hsv-root", className].filter(Boolean).join(" ")}
      style={{ ...cssVars, ...style }}
    >
      {/* Headline/hero area */}
      <div className="hsv-container" ref={headlineRef}>
        <div className="hsv-headline">
          <h1 className="hsv-title">{title}</h1>
          {subtitle ? <h2 className="hsv-subtitle">{subtitle}</h2> : null}
          {meta ? <div className="hsv-meta">{meta}</div> : null}
          {credits ? <div className="hsv-credits">{credits}</div> : null}
        </div>
      </div>

      {/* Sticky scroll section */}
      <div
        className="hsv-scroll"
        data-sticky-scroll
        style={{ height: scrollHeightVh > 0 ? `${Math.max(150, scrollHeightVh)}vh` : 0, overflow: scrollHeightVh <= 0 ? 'hidden' : undefined }}
      >
        <div className={`hsv-sticky ${sticky ? "is-sticky" : ""}`}>
          <div className="hsv-media" ref={containerRef}>
            {renderMedia()}

            {/* Mute/Unmute button */}
            {mediaType === "video" && (
              <button
                onClick={handleToggleMute}
                className="hsv-mute-btn"
                aria-label={isMuted ? "Unmute video" : "Mute video"}
              >
                {isMuted ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                    <line x1="23" y1="9" x2="17" y2="15"></line>
                    <line x1="17" y1="9" x2="23" y2="15"></line>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                  </svg>
                )}
              </button>
            )}

            {/* overlay that reveals */}
            <div className="hsv-overlay" ref={overlayRef}>
              {overlay?.caption ? (
                <div className="hsv-caption" ref={overlayCaptionRef}>
                  {overlay.caption}
                </div>
              ) : null}
              <div className="hsv-overlay-content" ref={overlayContentRef}>
                {overlay?.heading ? <h3>{overlay.heading}</h3> : null}
                {overlay?.paragraphs?.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
                {overlay?.extra}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Styles (scoped) - KF Brand themed */}
      <style>{`
        .hsv-root {
          --bg: #fafafa;
          --text: #18181b;
          --muted: #71717a;
          --muted-bg: rgba(24,24,27,0.06);
          --muted-border: rgba(24,24,27,0.12);
          --overlay-bg: rgba(10,10,14,0.55);
          --overlay-text: #ffffff;
          --accent: #c1272d;
          --accent-2: #a6383b;
          --shadow: 0 10px 30px rgba(0,0,0,0.08);

          background: var(--bg);
          color: var(--text);
          font-family: "Plus Jakarta Sans", Inter, ui-sans-serif, system-ui, -apple-system, sans-serif;
          overflow-x: clip;
        }

        .hsv-container {
          height: 100vh;
          display: grid;
          place-items: center;
          padding: clamp(16px, 3vw, 40px);
          perspective: 900px;
        }

        .hsv-headline {
          text-align: center;
          transform-style: preserve-3d;
          max-width: min(100%, 1100px);
        }
        .hsv-headline > * {
          transform-style: preserve-3d;
          backface-visibility: hidden;
          transform-origin: center top;
        }

        .hsv-title {
          margin: 0 0 .6rem 0;
          font-size: clamp(40px, 8vw, 96px);
          line-height: 0.9;
          font-weight: 900;
          letter-spacing: -0.02em;
          text-wrap: balance;
          font-family: "Oswald", ui-sans-serif, system-ui, sans-serif;
          color: var(--text);
          filter: drop-shadow(0 2px 0 rgba(0,0,0,0.05));
        }
        .hsv-subtitle {
          margin: 0 0 1.25rem 0;
          font-size: clamp(14px, 2.5vw, 18px);
          font-weight: 600;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--accent);
        }
        .hsv-meta {
          display: inline-flex;
          align-items: center;
          gap: .5rem;
          padding: .4rem .9rem;
          border-radius: 999px;
          font-size: .85rem;
          font-weight: 600;
          letter-spacing: .02em;
          background: var(--muted-bg);
          border: 1px solid var(--muted-border);
          box-shadow: var(--shadow);
          color: var(--text);
          margin: 1rem 0 0 0;
        }
        .hsv-meta::before {
          content: "";
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: linear-gradient(135deg, var(--accent), var(--accent-2));
          display: inline-block;
        }
        .hsv-credits {
          margin-top: 1.1rem;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--muted);
        }

        .hsv-scroll { position: relative; }
        .hsv-sticky.is-sticky {
          position: sticky;
          top: 0;
          height: 100vh;
          display: grid;
          place-items: center;
        }

        .hsv-media {
          position: relative;
          width: var(--initial-size);
          height: var(--initial-size);
          border-radius: 20px;
          overflow: hidden;
          background: #000;
          display: grid;
          place-items: center;
          transition: border-radius 0.3s ease;
          box-shadow: var(--shadow);
        }

        .hsv-overlay {
          position: absolute;
          inset: 0;
          background: var(--overlay-bg);
          color: var(--overlay-text);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: clamp(16px, 4vw, 40px);
          clip-path: inset(100% 0 0 0);
          backdrop-filter: blur(var(--overlay-blur));
          z-index: 2;
        }

        .hsv-caption {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          position: absolute;
          top: clamp(8px, 3vw, 24px);
          left: 0;
          width: 100%;
          text-align: center;
          opacity: 0.9;
          color: var(--accent);
        }

        .hsv-overlay-content {
          max-width: 68ch;
          display: grid;
          gap: 1rem;
        }
        .hsv-overlay-content h3 {
          font-size: clamp(28px, 5vw, 56px);
          line-height: 1.05;
          margin: 0;
          font-weight: 900;
          letter-spacing: -0.01em;
          font-family: "Oswald", ui-sans-serif, system-ui, sans-serif;
          color: #fff;
          text-wrap: balance;
        }
        .hsv-overlay-content p {
          font-size: clamp(15px, 2.1vw, 19px);
          line-height: 1.7;
          margin: 0;
          color: rgba(255,255,255,0.9);
        }

        .hsv-mute-btn {
          position: absolute;
          bottom: 16px;
          right: 16px;
          z-index: 10;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: rgba(0,0,0,0.6);
          border: none;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s ease, transform 0.2s ease;
          backdrop-filter: blur(4px);
        }
        .hsv-mute-btn:hover {
          background: rgba(0,0,0,0.8);
          transform: scale(1.1);
        }

        @media (max-width: 900px) {
          .hsv-overlay-content { max-width: 40ch; }
        }
        @media (max-width: 640px) {
          .hsv-media {
            max-width: 100vw !important;
          }
          .hsv-meta {
            max-width: 20rem;
            text-align: center;
            line-height: 1.4;
          }
        }
      `}</style>
    </div>
  )
})

export default HeroScrollVideo
